import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getWaitlistEntries, getWaitlistCount } from '../services/waitlistService';
import analyticsService from '../services/analyticsService';
import aiDebugService from '../services/aiDebugService';
import cacheService from '../services/cacheService';

const Admin = () => {
  const { getAllUsers, addUser, removeUser, updateUserByAdmin, refreshUsers, user } = useAuth();
  const [users, setUsers] = useState([]);
  const [showAddUser, setShowAddUser] = useState(false);
  const [waitlistEntries, setWaitlistEntries] = useState([]);
  const [waitlistCount, setWaitlistCount] = useState(0);
  const [activeTab, setActiveTab] = useState('users');
  
  const [analyticsMeasurementId, setAnalyticsMeasurementId] = useState(
    (() => {
      try {
        return analyticsService.getMeasurementId();
      } catch (error) {
        console.error('Error getting measurement ID:', error);
        return '';
      }
    })()
  );
  const [analyticsEnabled, setAnalyticsEnabled] = useState(
    (() => {
      try {
        return analyticsService.isAnalyticsEnabled();
      } catch (error) {
        console.error('Error checking analytics enabled:', error);
        return false;
      }
    })()
  );
  
  // Signup settings
  const [signupEnabled, setSignupEnabled] = useState(
    localStorage.getItem('bullbearly-signup-enabled') === 'true'
  );
  
  // AI Configuration state
  const [aiConfig, setAiConfig] = useState({
    masterPromptModel: 'gpt-4o-mini',
    customPromptModel: 'gpt-4o-mini',
    assistantId: 'asst_wdJ89zOBA2Tfo6R4KC9fUXcJ',
    debugMode: false
  });
  
  // AI Debug info state
  const [aiDebugInfo, setAiDebugInfo] = useState([]);
  const [cacheStats, setCacheStats] = useState({ total: 0, valid: 0, expired: 0 });
  
  // Function to refresh debug info
  const refreshDebugInfo = () => {
    const debugInfo = aiDebugService.getDebugInfo();
    console.log('Refreshing debug info:', debugInfo.length, 'entries');
    setAiDebugInfo(debugInfo);
  };

  // Function to refresh cache stats
  const refreshCacheStats = () => {
    const stats = cacheService.getCacheStats();
    setCacheStats(stats);
  };
  
  const [newUser, setNewUser] = useState({
    fullName: '',
    email: '',
    phone: '',
    password: '',
    role: 'USER',
    subscriptionType: 'EARLY_BIRD'
  });
  const [showEditUser, setShowEditUser] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editUser, setEditUser] = useState({
    fullName: '',
    email: '',
    phone: '',
    password: '',
    role: 'USER',
    subscriptionType: 'EARLY_BIRD'
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Analytics handlers
  const handleAnalyticsMeasurementIdChange = (e) => {
    setAnalyticsMeasurementId(e.target.value);
  };

  const handleAnalyticsSave = () => {
    analyticsService.updateMeasurementId(analyticsMeasurementId);
    setAnalyticsEnabled(true);
    analyticsService.trackSettingsChange('analytics_measurement_id', analyticsMeasurementId);
    alert('Analytics settings updated successfully!');
  };

  const handleAnalyticsToggle = () => {
    if (analyticsEnabled) {
      localStorage.removeItem('bullbearly-ga4-measurement-id');
      setAnalyticsEnabled(false);
    } else {
      analyticsService.initialize(analyticsMeasurementId);
      setAnalyticsEnabled(true);
    }
    analyticsService.trackSettingsChange('analytics_enabled', analyticsEnabled);
  };

  const handleSignupToggle = () => {
    const newValue = !signupEnabled;
    setSignupEnabled(newValue);
    localStorage.setItem('bullbearly-signup-enabled', newValue.toString());
    analyticsService.trackSettingsChange('signup_enabled', newValue);
  };

  // Load initial data
  useEffect(() => {
    const loadData = async () => {
      try {
        // Load users
        const usersData = getAllUsers();
        setUsers(usersData);
        
        // Load waitlist data
        const entries = await getWaitlistEntries();
        const count = await getWaitlistCount();
        setWaitlistEntries(entries);
        setWaitlistCount(count);
      } catch (error) {
        console.error('Error loading data:', error);
      }
    };

    loadData();
  }, [getAllUsers]);

  // Load AI configuration from localStorage
  useEffect(() => {
    const savedMasterModel = aiDebugService.getMasterPromptModel();
    const savedCustomModel = aiDebugService.getCustomPromptModel();
    const savedAssistantId = aiDebugService.getAssistantId();
    const savedDebugMode = aiDebugService.isDebugEnabled();
    
    setAiConfig({
      masterPromptModel: savedMasterModel,
      customPromptModel: savedCustomModel,
      assistantId: savedAssistantId,
      debugMode: savedDebugMode
    });
    
    // Load debug info if debug mode is enabled
    if (savedDebugMode) {
      refreshDebugInfo();
    }
    
    // Load cache stats
    refreshCacheStats();
  }, []);

  const handleAddUser = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      await addUser(newUser);
      // Refresh users from Firebase
      const updatedUsers = await refreshUsers();
      setUsers(updatedUsers);
      setNewUser({
        fullName: '',
        email: '',
        phone: '',
        password: '',
        role: 'USER',
        subscriptionType: 'EARLY_BIRD'
      });
      setShowAddUser(false);
      setSuccess('User added successfully!');
    } catch (error) {
      setError(error.message);
    }
  };

  const handleRemoveUser = async (userId) => {
    if (window.confirm('Are you sure you want to remove this user?')) {
      try {
        await removeUser(userId);
        // Refresh users from Firebase
        const updatedUsers = await refreshUsers();
        setUsers(updatedUsers);
        setSuccess('User removed successfully!');
      } catch (error) {
        setError(error.message);
      }
    }
  };

  const handleEditUser = (userToEdit) => {
    setEditingUser(userToEdit);
    setEditUser({
      fullName: userToEdit.fullName || '',
      email: userToEdit.email || '',
      phone: userToEdit.phone || '',
      password: '', // Don't pre-fill password
      role: userToEdit.role || 'USER',
      subscriptionType: userToEdit.subscriptionType || 'EARLY_BIRD'
    });
    setShowEditUser(true);
    setError('');
    setSuccess('');
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      // Prepare update data - only include fields that should be updated
      const updateData = {
        fullName: editUser.fullName,
        email: editUser.email,
        phone: editUser.phone,
        role: editUser.role,
        subscriptionType: editUser.subscriptionType
      };

      // Only include password if it's provided and user is not a Google user
      if (editUser.password && editingUser.authProvider !== 'google') {
        updateData.password = editUser.password;
      }

      await updateUserByAdmin(editingUser.id, updateData);
      
      // Refresh users from Firebase
      const updatedUsers = await refreshUsers();
      setUsers(updatedUsers);
      
      setShowEditUser(false);
      setEditingUser(null);
      setSuccess('User updated successfully!');
    } catch (error) {
      setError(error.message);
    }
  };

  const getRoleBadge = (role) => {
    return role === 'ADMIN' ? 'admin-badge' : 'user-badge';
  };

  const getSubscriptionBadge = (type) => {
    switch (type) {
      case 'EARLY_BIRD': return 'early-bird-badge';
      case 'MANAGED': return 'managed-badge';
      default: return 'basic-badge';
    }
  };

  const getPaymentStatusBadge = (status) => {
    switch (status) {
      case 'paid': return 'payment-paid-badge';
      case 'unpaid': return 'payment-unpaid-badge';
      case 'pending': return 'payment-pending-badge';
      default: return 'payment-unknown-badge';
    }
  };

  return (
    <div className="admin-container">
      <div className="admin-header">
        <h2>Admin Dashboard</h2>
        <div className="admin-tabs">
          <button 
            className={`admin-tab ${activeTab === 'users' ? 'active' : ''}`}
            onClick={() => setActiveTab('users')}
          >
            👥 Users ({users.length})
          </button>
          <button 
            className={`admin-tab ${activeTab === 'waitlist' ? 'active' : ''}`}
            onClick={() => setActiveTab('waitlist')}
          >
            📧 Waitlist ({waitlistCount})
          </button>
          <button 
            className={`admin-tab ${activeTab === 'analytics' ? 'active' : ''}`}
            onClick={() => setActiveTab('analytics')}
          >
            📊 Analytics
          </button>
          <button 
            className={`admin-tab ${activeTab === 'site-settings' ? 'active' : ''}`}
            onClick={() => setActiveTab('site-settings')}
          >
            ⚙️ Site Settings
          </button>
          <button 
            className={`admin-tab ${activeTab === 'ai-config' ? 'active' : ''}`}
            onClick={() => setActiveTab('ai-config')}
          >
            🤖 AI Config
          </button>
        </div>
      </div>

      {activeTab === 'users' && (
        <div className="admin-section">
          <div className="section-header">
            <h3>User Management</h3>
            <button 
              className="add-user-button"
              onClick={() => setShowAddUser(true)}
            >
              + Add User
            </button>
          </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      {showAddUser && (
        <div className="add-user-modal">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Add New User</h3>
              <button className="close-button" onClick={() => setShowAddUser(false)}>×</button>
            </div>
            
            <form onSubmit={handleAddUser} className="add-user-form">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="fullName">Full Name</label>
                  <input
                    type="text"
                    id="fullName"
                    value={newUser.fullName}
                    onChange={(e) => setNewUser({...newUser, fullName: e.target.value})}
                    required
                    placeholder="Enter full name"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="email">Email</label>
                  <input
                    type="email"
                    id="email"
                    value={newUser.email}
                    onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                    required
                    placeholder="Enter email"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="phone">Phone</label>
                  <input
                    type="tel"
                    id="phone"
                    value={newUser.phone}
                    onChange={(e) => setNewUser({...newUser, phone: e.target.value})}
                    required
                    placeholder="Enter phone number"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="password">Password</label>
                  <input
                    type="password"
                    id="password"
                    value={newUser.password}
                    onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                    required
                    placeholder="Enter password"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="role">Role</label>
                  <select
                    id="role"
                    value={newUser.role}
                    onChange={(e) => setNewUser({...newUser, role: e.target.value})}
                  >
                    <option value="USER">User</option>
                    <option value="ADMIN">Admin</option>
                  </select>
                </div>
                
                <div className="form-group">
                  <label htmlFor="subscriptionType">Subscription</label>
                  <select
                    id="subscriptionType"
                    value={newUser.subscriptionType}
                    onChange={(e) => setNewUser({...newUser, subscriptionType: e.target.value})}
                  >
                    <option value="EARLY_BIRD">Early Bird ($10/month)</option>
                    <option value="MANAGED">Managed ($19/month)</option>
                  </select>
                </div>
              </div>

              <div className="modal-actions">
                <button type="button" onClick={() => setShowAddUser(false)} className="cancel-button">
                  Cancel
                </button>
                <button type="submit" className="save-button">
                  Add User
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showEditUser && (
        <div className="add-user-modal">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Edit User</h3>
              <button className="close-button" onClick={() => setShowEditUser(false)}>×</button>
            </div>
            
            <form onSubmit={handleUpdateUser} className="add-user-form">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="editFullName">Full Name</label>
                  <input
                    type="text"
                    id="editFullName"
                    value={editUser.fullName}
                    onChange={(e) => setEditUser({...editUser, fullName: e.target.value})}
                    required
                    placeholder="Enter full name"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="editEmail">Email</label>
                  <input
                    type="email"
                    id="editEmail"
                    value={editUser.email}
                    onChange={(e) => setEditUser({...editUser, email: e.target.value})}
                    required
                    placeholder="Enter email"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="editPhone">Phone</label>
                  <input
                    type="tel"
                    id="editPhone"
                    value={editUser.phone}
                    onChange={(e) => setEditUser({...editUser, phone: e.target.value})}
                    placeholder="Enter phone number"
                  />
                </div>
                
                {editingUser && editingUser.authProvider !== 'google' && (
                  <div className="form-group">
                    <label htmlFor="editPassword">Password</label>
                    <input
                      type="password"
                      id="editPassword"
                      value={editUser.password}
                      onChange={(e) => setEditUser({...editUser, password: e.target.value})}
                      placeholder="Enter new password (leave blank to keep current)"
                    />
                  </div>
                )}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="editRole">Role</label>
                  <select
                    id="editRole"
                    value={editUser.role}
                    onChange={(e) => setEditUser({...editUser, role: e.target.value})}
                  >
                    <option value="USER">User</option>
                    <option value="ADMIN">Admin</option>
                  </select>
                </div>
                
                <div className="form-group">
                  <label htmlFor="editSubscriptionType">Subscription</label>
                  <select
                    id="editSubscriptionType"
                    value={editUser.subscriptionType}
                    onChange={(e) => setEditUser({...editUser, subscriptionType: e.target.value})}
                  >
                    <option value="EARLY_BIRD">Early Bird ($10/month)</option>
                    <option value="MANAGED">Managed ($19/month)</option>
                  </select>
                </div>
              </div>

              <div className="modal-actions">
                <button type="button" onClick={() => setShowEditUser(false)} className="cancel-button">
                  Cancel
                </button>
                <button type="submit" className="save-button">
                  Update User
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="users-table">
        <div className="table-header">
          <div className="header-cell">Name</div>
          <div className="header-cell">Email</div>
          <div className="header-cell">Phone</div>
          <div className="header-cell">Role</div>
          <div className="header-cell">Subscription</div>
          <div className="header-cell">Payment Status</div>
          <div className="header-cell">Last Payment</div>
          <div className="header-cell">Created</div>
          <div className="header-cell">Actions</div>
        </div>
        
        {users.map((userItem) => (
          <div key={userItem.id} className="table-row">
            <div className="table-cell">{userItem.fullName}</div>
            <div className="table-cell">{userItem.email}</div>
            <div className="table-cell">{userItem.phone}</div>
            <div className="table-cell">
              <span className={`role-badge ${getRoleBadge(userItem.role)}`}>
                {userItem.role}
              </span>
            </div>
            <div className="table-cell">
              <span className={`subscription-badge ${getSubscriptionBadge(userItem.subscriptionType)}`}>
                {userItem.subscriptionType.replace('_', ' ')}
              </span>
            </div>
            <div className="table-cell">
              {userItem.paymentInfo ? (
                <span className={`payment-status-badge ${getPaymentStatusBadge(userItem.paymentInfo.paymentStatus)}`}>
                  {userItem.paymentInfo.paymentStatus || 'Unknown'}
                </span>
              ) : (
                <span className="payment-status-badge no-payment">No Payment</span>
              )}
            </div>
            <div className="table-cell">
              {userItem.paymentInfo?.lastPaymentDate ? (
                <div className="payment-info">
                  <div>{new Date(userItem.paymentInfo.lastPaymentDate).toLocaleDateString()}</div>
                  {userItem.paymentInfo.subscriptionId && (
                    <div className="subscription-id" title={`Subscription ID: ${userItem.paymentInfo.subscriptionId}`}>
                      {userItem.paymentInfo.subscriptionId.substring(0, 20)}...
                    </div>
                  )}
                </div>
              ) : (
                <span className="no-payment">-</span>
              )}
            </div>
            <div className="table-cell">
              {userItem.createdAt ? new Date(userItem.createdAt).toLocaleDateString() : '-'}
            </div>
            <div className="table-cell">
              <div className="user-actions">
                <button
                  className="edit-user-button"
                  onClick={() => handleEditUser(userItem)}
                >
                  Edit
                </button>
                {user && userItem.id !== user.id && (
                  <button
                    className="remove-user-button"
                    onClick={() => handleRemoveUser(userItem.id)}
                  >
                    Remove
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
      </div>
      )}

      {activeTab === 'waitlist' && (
        <div className="admin-section">
          <div className="section-header">
            <h3>Waitlist Management</h3>
            <div className="waitlist-stats">
              <span className="stat">Total Entries: {waitlistCount}</span>
            </div>
          </div>

          <div className="waitlist-table">
            <div className="table-header">
              <div className="header-cell">Email</div>
              <div className="header-cell">Date Joined</div>
              <div className="header-cell">Status</div>
            </div>
            
            {waitlistEntries.map((entry) => (
              <div key={entry.id} className="table-row">
                <div className="table-cell">{entry.email}</div>
                <div className="table-cell">
                  {entry.timestamp ? new Date(entry.timestamp.toDate()).toLocaleDateString() : 'N/A'}
                </div>
                <div className="table-cell">
                  <span className={`status-badge ${entry.status}`}>
                    {entry.status}
                  </span>
                </div>
              </div>
            ))}
            
            {waitlistEntries.length === 0 && (
              <div className="no-data">
                <p>No waitlist entries yet.</p>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'analytics' && (
        <div className="admin-section">
          <div className="section-header">
            <h3>Analytics Configuration</h3>
          </div>
          
          <div className="admin-settings">
            <div className="setting-item">
              <label>
                <input 
                  type="checkbox" 
                  className="setting-checkbox" 
                  checked={analyticsEnabled || false}
                  onChange={handleAnalyticsToggle}
                />
                Enable Google Analytics 4 Tracking
              </label>
              <p className="setting-description">
                When enabled, tracks user interactions and page views for analytics insights.
              </p>
            </div>

            <div className="setting-item">
              <label htmlFor="analytics-measurement-id">GA4 Measurement ID</label>
              <div className="setting-control">
                <input
                  type="text"
                  id="analytics-measurement-id"
                  value={analyticsMeasurementId || ''}
                  onChange={handleAnalyticsMeasurementIdChange}
                  placeholder="G-XXXXXXXXXX"
                  className="setting-input"
                />
                <button 
                  onClick={handleAnalyticsSave}
                  className="setting-button"
                >
                  Save & Update
                </button>
              </div>
              <p className="setting-description">
                Your Google Analytics 4 Measurement ID (starts with G-). Get it from your GA4 property settings.
              </p>
            </div>

            <div className="analytics-status">
              <h3>Analytics Status</h3>
              <div className="status-item">
                <span className="status-label">Current Measurement ID:</span>
                <span className="status-value">{analyticsMeasurementId || 'Not set'}</span>
              </div>
              <div className="status-item">
                <span className="status-label">Tracking Status:</span>
                <span className={`status-value ${analyticsEnabled ? 'status-enabled' : 'status-disabled'}`}>
                  {analyticsEnabled ? '✅ Enabled' : '❌ Disabled'}
                </span>
              </div>
            </div>

            <div className="analytics-info">
              <h3>What We Track</h3>
              <ul className="analytics-features">
                <li>📊 Page views and user navigation</li>
                <li>📧 Waitlist signups and conversions</li>
                <li>🔑 User login and authentication events</li>
                <li>📰 RSS feed additions and usage</li>
                <li>🤖 AI prompt usage and interactions</li>
                <li>⚙️ Settings changes and preferences</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'site-settings' && (
        <div className="admin-section">
          <div className="section-header">
            <h3>Site Settings</h3>
          </div>
          
          <div className="admin-settings">
            <div className="setting-item">
              <label htmlFor="signup-enabled">
                <input
                  type="checkbox"
                  id="signup-enabled"
                  checked={signupEnabled || false}
                  onChange={handleSignupToggle}
                />
                Enable User Signups
              </label>
              <p>When enabled, users can create new accounts. When disabled, signup is not available.</p>
            </div>
            
            <div className="status-item">
              <strong>Current Status:</strong> {signupEnabled ? '✅ Signups Enabled' : '❌ Signups Disabled'}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'ai-config' && (
        <div className="admin-section">
          <div className="section-header">
            <h3>AI Configuration</h3>
          </div>
          
          <div className="admin-settings">
            <div className="setting-item">
              <label htmlFor="master-prompt-model">Master Prompt Model</label>
              <div className="setting-control">
                <select
                  id="master-prompt-model"
                  value={aiConfig.masterPromptModel}
                  onChange={(e) => setAiConfig({...aiConfig, masterPromptModel: e.target.value})}
                  className="setting-select"
                >
                  <option value="gpt-4o-mini">GPT-4o Mini</option>
                  <option value="gpt-4o">GPT-4o</option>
                  <option value="gpt-4-turbo">GPT-4 Turbo</option>
                  <option value="gpt-3.5-turbo">GPT-3.5 Turbo</option>
                </select>
                <button 
                  onClick={() => {
                    aiDebugService.setMasterPromptModel(aiConfig.masterPromptModel);
                    alert('Master prompt model updated!');
                  }}
                  className="setting-button"
                >
                  Save
                </button>
              </div>
              <p className="setting-description">
                The model used for the master prompt analysis. GPT-4o Mini is faster and cheaper.
              </p>
            </div>

            <div className="setting-item">
              <label htmlFor="custom-prompt-model">Custom Prompts Model</label>
              <div className="setting-control">
                <select
                  id="custom-prompt-model"
                  value={aiConfig.customPromptModel}
                  onChange={(e) => setAiConfig({...aiConfig, customPromptModel: e.target.value})}
                  className="setting-select"
                >
                  <option value="gpt-4o-mini">GPT-4o Mini</option>
                  <option value="gpt-4o">GPT-4o</option>
                  <option value="gpt-4-turbo">GPT-4 Turbo</option>
                  <option value="gpt-3.5-turbo">GPT-3.5 Turbo</option>
                </select>
                <button 
                  onClick={() => {
                    aiDebugService.setCustomPromptModel(aiConfig.customPromptModel);
                    alert('Custom prompts model updated!');
                  }}
                  className="setting-button"
                >
                  Save
                </button>
              </div>
              <p className="setting-description">
                The model used for custom prompt analysis (Risk Analysis, Opportunity Spotting, etc.).
              </p>
            </div>

            <div className="setting-item">
              <label htmlFor="assistant-id">OpenAI Assistant ID</label>
              <div className="setting-control">
                <input
                  type="text"
                  id="assistant-id"
                  value={aiConfig.assistantId}
                  onChange={(e) => setAiConfig({...aiConfig, assistantId: e.target.value})}
                  placeholder="asst_wdJ89zOBA2Tfo6R4KC9fUXcJ"
                  className="setting-input"
                />
                <button 
                  onClick={() => {
                    aiDebugService.setAssistantId(aiConfig.assistantId);
                    alert('Assistant ID updated!');
                  }}
                  className="setting-button"
                >
                  Save
                </button>
              </div>
              <p className="setting-description">
                The OpenAI Assistant ID used for company detection from news headlines.
              </p>
            </div>

            <div className="setting-item">
              <label>
                <input 
                  type="checkbox" 
                  className="setting-checkbox" 
                  checked={aiConfig.debugMode}
                  onChange={(e) => {
                    const newDebugMode = e.target.checked;
                    setAiConfig({...aiConfig, debugMode: newDebugMode});
                    aiDebugService.setDebugMode(newDebugMode);
                    if (newDebugMode) {
                      refreshDebugInfo();
                    } else {
                      setAiDebugInfo([]);
                    }
                  }}
                />
                Enable AI Debug Mode
              </label>
              <p className="setting-description">
                When enabled, AI requests and responses will be logged for debugging purposes.
              </p>
            </div>

            <div className="setting-item">
              <h3>Cache Management</h3>
              <div className="cache-stats">
                <div className="stat-item">
                  <span className="stat-label">Total Cache Entries:</span>
                  <span className="stat-value">{cacheStats.total}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Valid Entries:</span>
                  <span className="stat-value valid">{cacheStats.valid}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Expired Entries:</span>
                  <span className="stat-value expired">{cacheStats.expired}</span>
                </div>
              </div>
              <div className="cache-controls">
                <button 
                  onClick={() => {
                    cacheService.cleanExpiredCache();
                    refreshCacheStats();
                    alert('Expired cache entries cleaned up!');
                  }}
                  className="setting-button"
                >
                  Clean Expired Cache
                </button>
                <button 
                  onClick={() => {
                    if (window.confirm('Are you sure you want to clear all cache entries? This will force all API calls to be made again.')) {
                      cacheService.clearAllCache();
                      refreshCacheStats();
                      alert('All cache entries cleared!');
                    }
                  }}
                  className="setting-button danger"
                >
                  Clear All Cache
                </button>
                <button 
                  onClick={refreshCacheStats}
                  className="setting-button"
                >
                  Refresh Stats
                </button>
              </div>
              <p className="setting-description">
                Cache reduces API calls by storing headlines and stock data for 3 days. Clean expired entries to free up space.
              </p>
            </div>
          </div>

          {aiConfig.debugMode && (
            <div className="ai-debug-section">
              <h3>AI Debug Information</h3>
              <div className="debug-controls">
                <button 
                  onClick={() => {
                    aiDebugService.clearDebugInfo();
                    setAiDebugInfo([]);
                  }}
                  className="debug-button"
                >
                  Clear Debug Log
                </button>
                <button 
                  onClick={refreshDebugInfo}
                  className="debug-button"
                >
                  Refresh Debug Log
                </button>
                <button 
                  onClick={() => {
                    // Add a test debug entry
                    aiDebugService.logAiRequest(
                      'test',
                      'gpt-4o-mini',
                      'Test prompt for debugging',
                      'Test response from AI'
                    );
                    refreshDebugInfo();
                  }}
                  className="debug-button"
                >
                  Test Debug Log
                </button>
                <button 
                  onClick={() => {
                    const blob = new Blob([JSON.stringify(aiDebugInfo, null, 2)], { type: 'application/json' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `ai-debug-${new Date().toISOString().split('T')[0]}.json`;
                    a.click();
                    URL.revokeObjectURL(url);
                  }}
                  className="debug-button"
                >
                  Export Debug Log
                </button>
              </div>
              
              {aiDebugInfo.length === 0 ? (
                <div className="debug-empty">
                  <p>No AI debug information yet. AI requests will appear here when debug mode is enabled.</p>
                </div>
              ) : (
                <div className="debug-log">
                  {aiDebugInfo.map((entry, index) => (
                    <div key={index} className="debug-entry">
                      <div className="debug-entry-header">
                        <span className="debug-timestamp">{entry.timestamp}</span>
                        <span className="debug-type">{entry.type}</span>
                        <span className="debug-model">{entry.model}</span>
                      </div>
                      <div className="debug-content">
                        <div className="debug-section">
                          <h4>Prompt:</h4>
                          <pre className="debug-code">{entry.prompt}</pre>
                        </div>
                        <div className="debug-section">
                          <h4>Response:</h4>
                          <pre className="debug-code">{entry.response}</pre>
                        </div>
                        {entry.error && (
                          <div className="debug-section">
                            <h4>Error:</h4>
                            <pre className="debug-error">{entry.error}</pre>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Admin;
